import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, errorText } from '../api';
import { useAuth } from '../auth';
import { KundenAuswahl } from '../components/KundenAuswahl';
import { PositionenEditor, leerePosition, positionenFuerApi, type PositionEingabe } from '../components/Vorgang';
import { Card, Field, Notice } from '../components/ui';
import type { BenutzerKurz, Bestellung, Kunde, Standort } from '../types';

/** Kunde zuerst, dann Artikel - Zuständig und Wiedervorlage setzt das System. */
export function BestellungNeuPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [kunde, setKunde] = useState<Kunde | null>(null);
  const [positionen, setPositionen] = useState<PositionEingabe[]>([leerePosition()]);
  const [quelle, setQuelle] = useState('laden');
  const [standortId, setStandortId] = useState('');
  const [bereitsBestellt, setBereitsBestellt] = useState(false);
  const [lieferant, setLieferant] = useState('');
  const [erwartetAm, setErwartetAm] = useState('');
  const [notiz, setNotiz] = useState('');
  const [zustaendig, setZustaendig] = useState(user?.id ?? '');
  const [standorte, setStandorte] = useState<Standort[]>([]);
  const [benutzer, setBenutzer] = useState<BenutzerKurz[]>([]);
  const [fehler, setFehler] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api<Standort[]>('/api/standorte').then(setStandorte);
    void api<BenutzerKurz[]>('/api/benutzer/auswahl').then(setBenutzer);
    const kid = params.get('kunde');
    if (kid) void api<{ kunde: Kunde }>(`/api/kunden/${kid}`).then((r) => setKunde(r.kunde));
  }, [params]);

  async function anlegen() {
    if (!kunde) return;
    setBusy(true);
    setFehler('');
    try {
      const b = await api<Bestellung>('/api/bestellungen', {
        method: 'POST',
        body: {
          kundeId: kunde.id,
          standortId: standortId || null,
          quelle,
          positionen: positionenFuerApi(positionen),
          bereitsBestellt,
          lieferant: lieferant || null,
          erwartetAm: erwartetAm || null,
          notiz: notiz || null,
          zustaendigId: zustaendig,
        },
      });
      navigate(`/bestellungen/${b.id}`);
    } catch (e) {
      setFehler(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  const hatPosition = positionenFuerApi(positionen).length > 0;

  return (
    <>
      <div className="kopf">
        <h1>🛒 Neue Bestellung</h1>
      </div>
      {fehler && <Notice kind="error">{fehler}</Notice>}
      <div className="stack" style={{ maxWidth: 900 }}>
        <Card title="1. Kunde">
          <KundenAuswahl gewaehlt={kunde} onWahl={setKunde} />
        </Card>
        <Card title="2. Was wird bestellt?">
          <PositionenEditor positionen={positionen} onChange={setPositionen} />
        </Card>
        <Card title="3. Angaben">
          <div className="stack" style={{ gap: 12 }}>
            <div className="grid3">
              <Field label="Standort">
                <select value={standortId} onChange={(e) => setStandortId(e.target.value)}>
                  <option value="">—</option>
                  {standorte.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Woher">
                <select value={quelle} onChange={(e) => setQuelle(e.target.value)}>
                  <option value="laden">Im Laden</option>
                  <option value="telefon">Telefon</option>
                  <option value="email">E-Mail</option>
                  <option value="aussendienst">Außendienst</option>
                </select>
              </Field>
              <Field label="Zuständig">
                <select value={zustaendig} onChange={(e) => setZustaendig(e.target.value)}>
                  {benutzer.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                      {b.id === user?.id ? ' (ich)' : ''}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <label className="row">
              <input type="checkbox" style={{ width: 'auto' }} checked={bereitsBestellt} onChange={(e) => setBereitsBestellt(e.target.checked)} />
              Schon beim Lieferanten bestellt
            </label>
            {bereitsBestellt && (
              <div className="grid2">
                <Field label="Lieferant (Pflicht)">
                  <input value={lieferant} onChange={(e) => setLieferant(e.target.value)} />
                </Field>
                <Field label="Erwartet am" hint="Wiedervorlage dann am Tag danach">
                  <input type="date" value={erwartetAm} onChange={(e) => setErwartetAm(e.target.value)} />
                </Field>
              </div>
            )}
            <Field label="Notiz">
              <textarea value={notiz} onChange={(e) => setNotiz(e.target.value)} style={{ minHeight: 60 }} />
            </Field>
          </div>
        </Card>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button onClick={() => navigate(-1)}>Abbrechen</button>
          <button className="primary" disabled={busy || !kunde || !hatPosition || (bereitsBestellt && !lieferant.trim())} onClick={() => void anlegen()}>
            Bestellung anlegen
          </button>
        </div>
      </div>
    </>
  );
}
